import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { supabase } from '../../supabase';
import { useAuth } from '../../context/AuthContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const DailySpendingChart = () => {
  const { user } = useAuth();
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalSpent, setTotalSpent] = useState(0);
  const [activeDays, setActiveDays] = useState(0);

  // Memoize static values that don't change on each render
  const monthNames = useMemo(() => [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ], []);

  const getMonthDisplayName = useCallback((monthString) => {
    if (!monthString) return '';
    const [year, month] = monthString.split('-');
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  }, [monthNames]);

  const fetchData = useCallback(async () => {
    if (!user?.id) return;
    
    setLoading(true);
    
    try {
      const [year, month] = selectedMonth.split('-');
      const startDate = `${year}-${month}-01`;
      const endDate = `${year}-${month}-31`;
      
      const { data: bills } = await supabase
        .from('bill_extracts')
        .select('total_amount, receipt_date')
        .eq('user_id', user.id)
        .gte('receipt_date', startDate)
        .lte('receipt_date', endDate);
      
      const daysInMonth = new Date(year, month, 0).getDate();
      const dailyData = new Array(daysInMonth).fill(0);
      let total = 0;
      
      if (bills) {
        bills.forEach(bill => {
          const amount = parseFloat(bill.total_amount) || 0;
          total += amount;
          
          if (bill.receipt_date) {
            const date = new Date(bill.receipt_date);
            const day = date.getDate();
            if (day >= 1 && day <= daysInMonth) {
              dailyData[day - 1] += amount;
            }
          }
        });
      }
      
      const activeCount = dailyData.filter(amount => amount > 0).length;
      
      setChartData({
        labels: Array.from({ length: daysInMonth }, (_, i) => i + 1),
        datasets: [{
          label: 'Daily Spending',
          data: dailyData,
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          tension: 0.3,
          fill: true
        }]
      });
      
      setTotalSpent(total);
      setActiveDays(activeCount);
      
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id, selectedMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#333',
          font: {
            size: 14,
            weight: '600'
          }
        }
      },
      title: {
        display: true,
        text: `Daily Spending Overview - ${getMonthDisplayName(selectedMonth)}`,
        color: '#333',
        font: {
          size: 18,
          weight: 'bold'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgb(34, 197, 94)',
        borderWidth: 1,
        callbacks: {
          title: function(context) {
            const [year, month] = selectedMonth.split('-');
            return `${monthNames[parseInt(month) - 1]} ${context[0].label}, ${year}`;
          },
          label: function(context) {
            return `Amount: $${context.raw.toFixed(2)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: '#666',
          callback: function(value) {
            return '$' + value.toFixed(0);
          }
        }
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#666',
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index',
    }
  }), [selectedMonth, monthNames, getMonthDisplayName]);

  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }, []);

  const exportDailyToCSV = useCallback(() => {
    if (!chartData) return;

    const [year, month] = selectedMonth.split('-');
    
    const csvData = [
      ['Day', 'Amount ($)', 'Month', 'Year'],
      ...chartData.labels.map((day, index) => [
        day,
        chartData.datasets[0].data[index].toFixed(2),
        monthNames[parseInt(month) - 1],
        year
      ])
    ];

    csvData.push([]);
    csvData.push(['Summary']);
    csvData.push(['Total Spent', totalSpent.toFixed(2)]);
    csvData.push(['Active Days', activeDays]);

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `daily_spending_${selectedMonth}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [chartData, selectedMonth, monthNames, totalSpent, activeDays]);

  return (
    <div style={{ 
      backgroundColor: '#fff', 
      padding: '25px', 
      borderRadius: '15px', 
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      margin: '20px 0'
    }}>
      {/* Header with Month Selector */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <h3 style={{ 
          color: '#333', 
          margin: 0, 
          fontSize: '24px',
          fontWeight: 'bold'
        }}>
          Daily Spending Analytics
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <label htmlFor="month-picker" style={{ 
            color: '#666', 
            fontWeight: '600',
            fontSize: '14px'
          }}>
            Month:
          </label>
          <input
            id="month-picker"
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            min="2021-01"
            max={new Date().toISOString().slice(0, 7)}
            style={{
              padding: '8px 15px',
              borderRadius: '8px',
              border: '2px solid #e1e5e9',
              backgroundColor: '#fff',
              color: '#333',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              outline: 'none',
              transition: 'border-color 0.2s ease'
            }}
            onFocus={(e) => e.target.style.borderColor = 'rgb(34, 197, 94)'}
            onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
          />
          <button
            onClick={exportDailyToCSV}
            disabled={!chartData || loading}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '2px solid rgb(34, 197, 94)',
              backgroundColor: !chartData || loading ? '#f8f9fa' : 'rgb(34, 197, 94)',
              color: !chartData || loading ? '#666' : '#fff',
              fontSize: '14px',
              fontWeight: '600',
              cursor: !chartData || loading ? 'not-allowed' : 'pointer',
              outline: 'none',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
            onMouseEnter={(e) => {
              if (!loading && chartData) {
                e.target.style.backgroundColor = 'rgb(21, 128, 61)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading && chartData) {
                e.target.style.backgroundColor = 'rgb(34, 197, 94)';
              }
            }}
          >
            📊 Export CSV
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
        gap: '10px', 
        marginBottom: '20px' 
      }}>
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '10px',
          borderRadius: '8px',
          textAlign: 'center',
          border: '1px solid #e9ecef',
          minHeight: '85px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'rgb(34, 197, 94)' }}>
            {formatCurrency(totalSpent)}
          </div>
          <div style={{ fontSize: '10px', color: '#666', marginTop: '3px' }}>
            Total Spent ({getMonthDisplayName(selectedMonth)})
          </div>
        </div>
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '10px',
          borderRadius: '8px',
          textAlign: 'center',
          border: '1px solid #e9ecef',
          minHeight: '85px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#28a745' }}>
            {activeDays}
          </div>
          <div style={{ fontSize: '10px', color: '#666', marginTop: '3px' }}>
            Active Days
          </div>
        </div>
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '10px',
          borderRadius: '8px',
          textAlign: 'center',
          border: '1px solid #e9ecef',
          minHeight: '85px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#ffc107' }}>
            {formatCurrency(activeDays > 0 ? totalSpent / activeDays : 0)}
          </div>
          <div style={{ fontSize: '10px', color: '#666', marginTop: '3px' }}>
            Average Per Active Day
          </div>
        </div>
      </div>

      {/* Chart */}
      <div style={{ height: '400px', marginBottom: '15px' }}>
        {loading ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            color: '#666',
            fontSize: '16px'
          }}>
            Loading daily spending data...
          </div>
        ) : chartData ? (
          <Line data={chartData} options={chartOptions} />
        ) : (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            color: '#666',
            fontSize: '16px',
            textAlign: 'center'
          }}>
            <div>
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>📈</div>
              <div>No spending data for {getMonthDisplayName(selectedMonth)}</div>
              <div style={{ fontSize: '14px', marginTop: '5px', color: '#999' }}>
                Upload some bills to see your daily spending patterns
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div style={{ 
        fontSize: '12px', 
        color: '#666', 
        textAlign: 'center',
        borderTop: '1px solid #e9ecef',
        paddingTop: '15px'
      }}>
        Daily spending patterns for {getMonthDisplayName(selectedMonth)}. Hover over chart points for detailed information.
      </div>
    </div>
  );
};

export default DailySpendingChart;
